import React from 'react';
import type { Etudiant } from '../../services/etudiantsApi';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface InscritsGroup {
  groupTitle: string;
  students: Etudiant[];
  effectif: number;
}

// ─── Helper ────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined): string => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

// ─── Colonnes ──────────────────────────────────────────────────────────────

const COLUMNS = [
  { label: 'N°', key: 'num', width: '30px', align: 'center' },
  { label: 'Matricule', key: 'matricule', width: '10%', align: 'center' },
  { label: 'Nom(s)', key: 'nom', width: '17%' },
  { label: 'Prénom(s)', key: 'prenoms', width: '17%' },
  { label: 'Date de Naissance', key: 'date_naissance', width: '10%', align: 'center' },
  { label: 'Lieu de Naissance', key: 'lieu_naissance', width: '15%' },
  { label: 'Sexe', key: 'sexe', width: '35px', align: 'center' },
  { label: 'Classe', key: 'classe', width: '8%', align: 'center' },
];

// ─── buildListeInscritsPageHTML — une page par groupe ──────────────────────

function buildListeInscritsPageHTML(
  groupTitle: string,
  students: Etudiant[],
  anneeAcademique: string,
  effectif: number,
): string {
  const tableRows = students.map((s, i) => {
    const bgColor = i % 2 === 0 ? '#fff' : '#f5f5f5';
    const cells = COLUMNS.map(col => {
      let val = '';
      switch (col.key) {
        case 'num': val = String(i + 1); break;
        case 'matricule': val = s.matricule; break;
        case 'nom': val = s.nom; break;
        case 'prenoms': val = s.prenoms; break;
        case 'date_naissance': val = fmtDate(s.date_naissance); break;
        case 'lieu_naissance': val = s.lieu_naissance || ''; break;
        case 'sexe': val = s.sexe || ''; break;
        case 'classe': val = s.classe || ''; break;
      }
      return `<td style="border:1px solid #000;padding:4px 6px;text-align:${col.align || 'left'};">${val}</td>`;
    }).join('');
    return `<tr style="background:${bgColor};">${cells}</tr>`;
  }).join('');

  const colHeaders = COLUMNS.map(col =>
    `<th style="border:1px solid #000;padding:4px 6px;font-weight:bold;text-align:center;background:#FFD700;${col.width ? 'width:' + col.width + ';' : ''}">${col.label}</th>`
  ).join('');

  return `
<!-- En-tête institutionnel -->
<table style="width:100%;margin-bottom:6px;"><tr>
  <td style="width:80px;vertical-align:top;text-align:center;">
    <img src="/logo.png" alt="Logo" style="width:65px;height:65px;object-fit:contain;" onerror="this.style.display='none'" />
  </td>
  <td style="vertical-align:top;text-align:center;line-height:1.4;">
    <div class="inst-name" style="font-weight:bold;text-transform:uppercase;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
    <div style="font-size:9px;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
    <div style="font-weight:bold;font-size:11px;margin-top:6px;text-transform:uppercase;">SERVICE DE LA SCOLARITE ET DES EXAMENS</div>
  </td>
</tr></table>

<!-- Titre -->
<div class="title" style="text-align:center;font-weight:bold;margin:10px 0 4px 0;text-transform:uppercase;text-decoration:underline;">
  LISTE PROVISOIRE DES INSCRITS
</div>

<!-- Sous-titre -->
<div class="subtitle" style="text-align:center;margin-bottom:8px;">
  <span style="margin-right:20px;">Année Académique : <strong>${anneeAcademique}</strong></span>
  <span>${groupTitle}</span>
  <span style="margin-left:20px;">Effectif : <strong>${effectif}</strong></span>
</div>

<!-- Tableau -->
<table style="width:100%;border-collapse:collapse;">
  <thead><tr>${colHeaders}</tr></thead>
  <tbody>${tableRows}</tbody>
</table>

<!-- Signature -->
<div style="margin-top:25px;text-align:right;font-size:10px;">
  <div>Fait à Pointe-Noire, le ...................................</div>
  <div style="margin-top:10px;font-weight:bold;">Le Chef de Service de la Scolarité &amp; des Examens</div>
  <div style="margin-top:35px;">................................................................</div>
  <div style="margin-top:2px;font-weight:bold;">Donald Christ MBEMBA LOKO</div>
</div>
`;
}

// ─── buildListeInscritsHTML — génère le HTML complet multi-pages ──────────

export type PageSize = 'A4' | 'A3';

export function buildListeInscritsHTML(
  groups: InscritsGroup[],
  anneeAcademique: string,
  triLabel: string,
  pageSize: PageSize = 'A4',
): string {
  const isA3 = pageSize === 'A3';
  const fontSize = isA3 ? '14px' : '12px';
  const headerFontSize = isA3 ? '12px' : '11px';
  const titleSize = isA3 ? '20px' : '16px';
  const institutSize = isA3 ? '16px' : '13px';
  const subSize = isA3 ? '12px' : '10px';
  const marginPage = isA3 ? '12mm 15mm' : '10mm 12mm';
  const pages = groups.map((group, i) => {
    const pageHTML = buildListeInscritsPageHTML(
      group.groupTitle,
      group.students,
      anneeAcademique,
      group.effectif,
    );
    const pageBreak = i < groups.length - 1 ? 'page-break-after:always;' : '';
    return `<div style="${pageBreak}">${pageHTML}
      <div style="margin-top:8px;font-size:8px;display:flex;justify-content:space-between;color:#666;">
        <span>Institut International 2i</span>
        <span>liste provisoire des inscrits — ${triLabel}</span>
        <span>${i + 1}/${groups.length}</span>
      </div>
    </div>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>LISTE PROVISOIRE DES INSCRITS</title>
<style>
  @page { size: ${pageSize} portrait; margin: ${marginPage}; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; }
  }
  body {
    font-family: 'Agency FB', Arial, Helvetica, sans-serif;
    font-size: ${isA3 ? '15px' : '13px'};
    color: #000;
    margin: 0;
    padding: 10px 15px;
  }
  table { border-collapse: collapse; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  td { font-size: ${fontSize}; }
  th { font-size: ${headerFontSize}; }
  .inst-name { font-size: ${institutSize}; }
  .title { font-size: ${titleSize}; }
  .subtitle { font-size: ${subSize}; }
</style>
</head>
<body>
${pages}
</body>
</html>`;
}

// ─── Composant React pour aperçu iframe ─────────────────────────────────

interface ListeInscritsPreviewProps {
  html: string;
}

const ListeInscritsPreview: React.FC<ListeInscritsPreviewProps> = ({ html }) => {
  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <iframe
        srcDoc={html}
        style={{ width: '100%', minHeight: '80vh', border: 'none' }}
        title="Aperçu Liste Provisoire des Inscrits"
      />
    </div>
  );
};

export default ListeInscritsPreview;
